import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Trash2, Loader, CheckCircle, Building2, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { BACKEND_URL } from '../../config';
import { getAdminAuthToken } from '../../utils/adminAuth';
import { toast } from 'sonner';

const UserPurgeModal = ({ user, onClose, onSuccess }) => {
  const [step, setStep] = useState('analysis'); // 'analysis', 'confirmation', 'purging', 'complete'
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmationText, setConfirmationText] = useState('');
  const [reason, setReason] = useState('');
  const [purgeResult, setPurgeResult] = useState(null);

  useEffect(() => {
    fetchImpact();
  }, []);

  const fetchImpact = async () => {
    try {
      setLoading(true);
      const token = getAdminAuthToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users/${user.id}/purge-impact`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImpact(response.data);
    } catch (error) {
      console.error('Error fetching impact:', error);
      toast.error('Failed to analyze purge impact');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handlePurge = async () => {
    try {
      setStep('purging');
      const token = getAdminAuthToken();
      const response = await axios.delete(
        `${BACKEND_URL}/api/admin/users/${user.id}/purge`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            confirmation: `DELETE ${user.email}`,
            reason: reason || undefined
          }
        }
      );
      setPurgeResult(response.data);
      setStep('complete');
      toast.success('User purged successfully');
    } catch (error) {
      console.error('Error purging user:', error);
      toast.error(error.response?.data?.detail || 'Failed to purge user');
      setStep('confirmation');
    }
  };

  const expectedConfirmation = `DELETE ${user.email}`;
  const isConfirmationValid = confirmationText === expectedConfirmation;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        {/* Analysis Step */}
        {step === 'analysis' && (
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle size={24} className="text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
                    Purge User Account
                  </h2>
                  <p className="text-sm text-gray-600">{user.email}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="animate-spin" size={32} style={{ color: '#6F5BEB' }} />
                <span className="ml-3 text-gray-600">Analyzing impact...</span>
              </div>
            ) : impact && (
              <div>
                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  <h3 className="font-bold text-lg mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
                    📊 Impact Analysis
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-2xl font-bold" style={{ color: '#6F5BEB' }}>
                        {impact.impact.claims}
                      </p>
                      <p className="text-xs text-gray-600">Claims</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-2xl font-bold" style={{ color: '#FF7A1A' }}>
                        {impact.impact.redemptions}
                      </p>
                      <p className="text-xs text-gray-600">Redemptions</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-2xl font-bold" style={{ color: '#10B981' }}>
                        {impact.impact.businesses_owned}
                      </p>
                      <p className="text-xs text-gray-600">Businesses Owned</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-2xl font-bold" style={{ color: '#EF4444' }}>
                        {impact.impact.deals_created}
                      </p>
                      <p className="text-xs text-gray-600">Deals Created</p>
                    </div>
                  </div>

                  {impact.details.businesses.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold mb-2">Businesses:</p>
                      {impact.details.businesses.map((biz) => (
                        <div key={biz.id} className="flex items-center gap-2 text-sm text-gray-700 ml-4">
                          <Building2 size={14} />
                          <span>{biz.name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {impact.details.hubs.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold mb-2">Hub Locations:</p>
                      {impact.details.hubs.map((hub) => (
                        <div key={hub.id} className="flex items-center gap-2 text-sm text-gray-700 ml-4">
                          <Building2 size={14} />
                          <span>{hub.name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <span className="font-bold">Total Items to Delete:</span>
                      <span className="text-2xl font-bold" style={{ color: '#EF4444' }}>
                        {impact.impact.total_items}
                      </span>
                    </div>
                  </div>
                </div>

                {impact.warnings.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-yellow-800 mb-2">Warnings:</p>
                        <ul className="space-y-1">
                          {impact.warnings.map((warning, index) => (
                            <li key={index} className="text-sm text-yellow-700">• {warning}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <p className="text-red-800 font-semibold text-center">
                    ⚠️ This action CANNOT be undone!
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => setStep('confirmation')}
                    className="flex-1"
                    style={{ backgroundColor: '#EF4444', color: 'white' }}
                  >
                    Continue to Confirmation
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Confirmation Step */}
        {step === 'confirmation' && (
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle size={24} className="text-red-600" />
                </div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
                  Final Confirmation
                </h2>
              </div>
              <button
                onClick={() => setStep('analysis')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
              <p className="text-red-800 font-semibold mb-4">
                You are about to permanently delete:
              </p>
              <ul className="space-y-2 text-red-700">
                <li>• User: <strong>{user.email}</strong></li>
                <li>• <strong>{impact?.impact.total_items}</strong> associated records</li>
                <li>• All claims, redemptions, and activity</li>
                {impact?.impact.businesses_owned > 0 && (
                  <li>• <strong>{impact.impact.businesses_owned}</strong> business(es) and their deals</li>
                )}
              </ul>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                Reason for Purge (Optional)
              </label>
              <Input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Test account cleanup"
                className="mb-4"
              />

              <label className="block text-sm font-semibold mb-2">
                Type <code className="bg-gray-200 px-2 py-1 rounded">{expectedConfirmation}</code> to confirm:
              </label>
              <Input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={expectedConfirmation}
                className={`${
                  confirmationText && !isConfirmationValid 
                    ? 'border-red-500 focus:ring-red-500' 
                    : ''
                }`}
              />
              {confirmationText && !isConfirmationValid && (
                <p className="text-red-600 text-sm mt-2">Confirmation does not match</p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep('analysis')}
                className="flex-1"
              >
                Go Back
              </Button>
              <Button
                onClick={handlePurge}
                disabled={!isConfirmationValid}
                className="flex-1"
                style={{ 
                  backgroundColor: isConfirmationValid ? '#EF4444' : '#E5E7EB',
                  color: isConfirmationValid ? 'white' : '#9CA3AF',
                  cursor: isConfirmationValid ? 'pointer' : 'not-allowed'
                }}
              >
                <Trash2 size={18} className="mr-2" />
                PURGE USER
              </Button>
            </div>
          </div>
        )}

        {/* Purging Step */}
        {step === 'purging' && (
          <div className="p-6 sm:p-8">
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="animate-spin mb-4" size={48} style={{ color: '#EF4444' }} />
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                Purging User...
              </h3>
              <p className="text-gray-600">Please wait, this may take a moment</p>
            </div>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && purgeResult && (
          <div className="p-6 sm:p-8">
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                User Purged Successfully
              </h3>
              <p className="text-gray-600 mb-6 text-center">
                {purgeResult.message}
              </p>

              <div className="bg-gray-50 rounded-xl p-6 w-full mb-6">
                <h4 className="font-semibold mb-4">Deletion Summary:</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {Object.entries(purgeResult.summary.items_deleted).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                      <span className="font-semibold">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={() => {
                  onSuccess();
                  onClose();
                }}
                style={{ backgroundColor: '#6F5BEB', color: 'white' }}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPurgeModal;
